import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import type { SyncQueue } from "@norkron/web-sync-queue";
import {
  ConfirmationBus,
  getSyncQueue,
  type SyncQueueBundle,
} from "./queue";
import { startRestOutbox, stopRestOutbox } from "./restOutbox";
import type { TempIdRegistry } from "./tempIds";

/**
 * `SyncProvider` — wires the app-wide {@link SyncQueue} lifecycle (design §4.0).
 * Mount it at the app root (alongside `ToastProvider`/`AuthProvider`); it
 * `start()`s the queue on mount and `stop()`s on unmount. Screens read the queue
 * via {@link useSyncQueue} to route writes (`mutations.ts`) and the offline
 * indicator reads it via `useSyncStatus`.
 */

interface SyncCtx {
  queue: SyncQueue;
  bus: ConfirmationBus;
  registry: TempIdRegistry;
}

const Ctx = createContext<SyncCtx | null>(null);

export function SyncProvider({
  children,
  bundle,
}: {
  children: ReactNode;
  /** Injected bundle (tests); defaults to the process singleton. */
  bundle?: SyncQueueBundle;
}) {
  const resolved = useMemo(() => bundle ?? getSyncQueue(), [bundle]);

  useEffect(() => {
    void resolved.queue.start();
    // The REST-replay outbox (reactions + comment edit/create) shares the same
    // app lifecycle: start draining + connectivity detection on mount.
    startRestOutbox();
    return () => {
      resolved.queue.stop();
      stopRestOutbox();
    };
  }, [resolved]);

  const value = useMemo<SyncCtx>(
    () => ({ queue: resolved.queue, bus: resolved.bus, registry: resolved.registry }),
    [resolved],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The app's SyncQueue + confirmation bus + temp-id registry. */
export function useSync(): SyncCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSync must be used within <SyncProvider>");
  return ctx;
}

/** Just the queue (the common case — route a write, read status). */
export function useSyncQueue(): SyncQueue {
  return useSync().queue;
}
